import {
    checkNullValues,
    formDataToObject,
    IKeyValue,
    ISharedConfig,
    setSnackbar
} from "@shared/library";
import axios, { AxiosError } from "axios";
import React, { JSX } from "react";
import config from "../../../../../../../../config";
import { composeName } from "../../methods";
import { customDataPrefix } from "../../static";
import "./InterventionEditForm.scss";
//#region ICONS
import { GasMeterOutlined, Settings } from "@mui/icons-material";
import ConstructionRoundedIcon from '@mui/icons-material/ConstructionRounded';
import ManageAccountsRoundedIcon from '@mui/icons-material/ManageAccountsRounded';
import RoomRoundedIcon from '@mui/icons-material/RoomRounded';
//#endregion
//#region COMPONENTS
import GestSaveButton from "@shared/library/dist/components/gest/GestInputs/button/save/GestSaveButton";
import GestDatePicker from "@shared/library/dist/components/gest/GestInputs/pickers/date/GestDatePicker";
import GestSelect from "@shared/library/dist/components/gest/GestInputs/select/GestSelect";
import GestTextArea from "@shared/library/dist/components/gest/GestInputs/text area/GestTextArea";
//#endregion
//#region MODELS
import { IGestSelectOption } from "@shared/library/dist/components/gest/GestInputs/select/GestSelectProps";
import { IIntervention, InterventionEdit } from "../../_models/Intervention";
import { IWfmOptions } from "../InterventionManagement";
//#endregion

export interface IInterventionEditFormProps {
    config: ISharedConfig,
    intervention: IIntervention,
    onSuccess: (i: IIntervention) => void,
    options?: IWfmOptions,
    anomalies: { id: number | string, description?: string; }[],
    lang: any;
    customInterventionEditForm?: (props: IInterventionEditFormProps) => JSX.Element;
};

const InterventionEditForm = (props: IInterventionEditFormProps) => {
    //STATES
    const editForm = React.useRef<HTMLFormElement>(null);
    const [interventionEditLoading, setInterventionsEditLoading] = React.useState(false);

    // METHODS
    const editIntervention = (event: React.SubmitEvent<HTMLFormElement>, oldObject: IIntervention) => {
        event.preventDefault();

        const _ = require("lodash");
        const formData = formDataToObject<InterventionEdit>(editForm.current, true);
        const additiveData = Object
            .keys(formData ?? {})
            .reduce((acc, k) => {
                if (k.toLowerCase().includes(customDataPrefix) && !acc[k]) {
                    const key = k.replace(customDataPrefix, "");
                    acc[key] = formData![k as keyof (InterventionEdit)];
                }

                return acc;
            }, {} as IKeyValue<any>);
        const newObject: IIntervention = {
            ...oldObject,
            ...formData,
            custom_data: Object.keys(additiveData).length ? JSON.stringify(additiveData) : undefined
        };

        if (newObject.status !== "processed" || (newObject.new_brand && newObject.new_serial)) {
            setInterventionsEditLoading(true);
            axios.patch<IIntervention>(`${config.api.internal}/WFM/Intervention/${oldObject.id}/Edit`, newObject, { withCredentials: true })
                .then(res => {
                    props.onSuccess(res.data);
                    setSnackbar(props.config, { message: props.lang.successfully_updated, severity: 'success' });
                })
                .catch((err: AxiosError<{ detail?: string; }>) => {
                    console.error(err);
                    setSnackbar(props.config, { message: "Errore durante modifica dell'intervento" + (err.response?.data?.detail?.toLowerCase().includes("no change") ? ": nessun cambiamento apportato nel form" : ""), severity: 'error' });
                })
                .finally(() => setInterventionsEditLoading(false));
        }
        else {
            const isInvalidStatus = newObject.status === "processed" && (!newObject.new_brand || !newObject.new_serial);
            setSnackbar(props.config, { message: isInvalidStatus ? "Per impostare un processato è necessario inserire marca e seriale nuovi" : "Errore di conversione form", severity: 'warning' });
        }
    };

    return props.intervention && props.options &&
        <form
            id="interventionEditForm"
            ref={editForm}
            onSubmit={evt => editIntervention(evt, props.intervention!)}
        >
            {props.customInterventionEditForm?.(props) ??
                <>
                    <GestSelect
                        required
                        name="plan_id"
                        w={300}
                        label={props.lang.plan}
                        value={checkNullValues(props.intervention.plan_id)}
                        options={props.options.plans.map(v => ({ value: v.id, label: composeName(v) }))}
                    />

                    <fieldset className="section" id="user">
                        <legend><ManageAccountsRoundedIcon /> {props.lang.registry_details}</legend>

                        <GestTextArea fullw type="number" label="CIU" defaultValue={checkNullValues(props.intervention.ciu)} disabled />
                        <GestTextArea fullw name="email" type="email" label={props.lang.email} defaultValue={checkNullValues(props.intervention.email)} />
                        <GestTextArea fullw name="accountholder" label={props.lang.accountholder} defaultValue={checkNullValues(props.intervention.accountholder)} />
                        <GestTextArea fullw name="identity_code" type="number" label={props.lang.identity_code} defaultValue={checkNullValues(props.intervention.identity_code)} />
                        <GestTextArea fullw name="telephone" type="tel" label={props.lang.telephone} defaultValue={checkNullValues(props.intervention.telephone)} />
                    </fieldset>

                    <fieldset className="section" id="geographics">
                        <legend><RoomRoundedIcon /> {props.lang.geo_data}</legend>

                        <GestTextArea fullw name="lat" type="number" label={props.lang.latitude} defaultValue={checkNullValues(props.intervention.lat)} />
                        <GestTextArea fullw name="district" label={props.lang.district} defaultValue={checkNullValues(props.intervention.district)} />
                        <GestSelect
                            fullw
                            name="old_location"
                            label={props.lang.old_location}
                            value={checkNullValues(props.intervention.old_location)}
                            options={props.options?.meterLocations}
                        />
                        <GestTextArea fullw name="lng" type="number" label={props.lang.longitude} defaultValue={checkNullValues(props.intervention.lng)} />
                        <GestTextArea fullw name="address" label={props.lang.address} defaultValue={checkNullValues(props.intervention.address)} />
                        <GestSelect
                            fullw
                            name="new_location"
                            label={props.lang.new_location}
                            value={checkNullValues(props.intervention.new_location)}
                            options={props.options?.meterLocations}
                        />
                        <GestTextArea fullw name="alt" type="number" label={props.lang.altitude} defaultValue={checkNullValues(props.intervention.alt)} />
                    </fieldset>

                    <fieldset className="section">
                        <legend><GasMeterOutlined /> {props.lang.meter}</legend>

                        <GestSelect
                            name="old_brand"
                            fullw
                            label={props.lang.old_brand}
                            value={checkNullValues(props.intervention.old_brand)}
                            options={props.options?.meterBrands}
                        />
                        <GestTextArea fullw name="old_serial" label={props.lang.old_serial} defaultValue={checkNullValues(props.intervention.old_serial)} />
                        <GestTextArea fullw name="old_reading" type="number" label={props.lang.old_reading} defaultValue={checkNullValues(props.intervention.old_reading)} />
                        <GestSelect
                            name="anomaly"
                            fullw
                            label={props.lang.anomaly}
                            value={checkNullValues(props.intervention.anomaly)}
                            options={props.anomalies.map(a => ({ value: a.id, label: a.description }))}
                        />

                        <GestSelect
                            name="new_brand"
                            fullw
                            label={props.lang.new_brand}
                            value={checkNullValues(props.intervention.new_brand)}
                            options={props.options?.meterBrands}
                        />
                        <GestTextArea fullw name="new_serial" label={props.lang.new_serial} defaultValue={checkNullValues(props.intervention.new_serial)} />
                        <GestTextArea fullw name="new_reading" type="number" label={props.lang.new_reading} defaultValue={checkNullValues(props.intervention.new_reading)} />
                        <GestTextArea fullw name="anomaly_notes" label={props.lang.anomaly_notes} defaultValue={checkNullValues(props.intervention.anomaly_notes)} />

                        <GestTextArea fullw name="seal_code" label={props.lang.seal_code} defaultValue={checkNullValues(props.intervention.seal_code)} />
                        <GestSelect
                            name="valves"
                            fullw
                            label={props.lang.valve}
                            value={checkNullValues(props.intervention.valves)}
                            options={props.options?.meterValves.map(v => ({ value: v.code, label: props.lang[v.code] ?? v.label }))}
                        />
                        <GestTextArea fullw name="new_digits" type="number" label={props.lang.digits_number} defaultValue={checkNullValues(props.intervention.new_digits)} />
                        <GestTextArea fullw name="diameter" type="number" minLen={0} label={props.lang.diameter_mm} defaultValue={checkNullValues(props.intervention.diameter)} />
                    </fieldset>

                    <fieldset className="section">
                        <legend><ConstructionRoundedIcon /> {props.lang.intervention} - {props.lang[props.intervention.type]}</legend>

                        <GestTextArea fullw type="number" label="ID" value={checkNullValues(props.intervention.id)} disabled />
                        <GestDatePicker name="intervention_start_date" label={props.lang.intervention_start_date} value={props.intervention.intervention_start_date} datetime />
                        <GestSelect
                            name="specifics"
                            fullw
                            label={props.lang.intervention_specifics}
                            value={checkNullValues(props.intervention.specifics)}
                            options={props.options?.interventionSpecifics}
                        />
                        <GestTextArea fullw name="annotations" label={props.lang.annotations} defaultValue={checkNullValues(props.intervention.annotations)} />

                        <GestTextArea fullw name="causal" label={props.lang.causal} defaultValue={checkNullValues(props.intervention.causal)} />
                        <GestDatePicker name="intervention_end_date" label={props.lang.intervention_end_date} value={props.intervention.intervention_end_date} datetime />
                        <GestSelect
                            name="result"
                            fullw
                            style={{ width: 250, maxWidth: 250 }}
                            label={props.lang.intervention_result}
                            value={checkNullValues(props.intervention.result)}
                            options={props.options?.interventionSpecifics}
                        />
                        <GestTextArea fullw name="notes" label={props.lang.notes} defaultValue={checkNullValues(props.intervention.notes)} />

                        <GestSelect
                            required
                            name="status"
                            fullw
                            className={props.intervention.status}
                            label={props.lang.intervention_status}
                            value={checkNullValues(props.intervention.status)}
                            options={props.options?.interventionStatuses.map<IGestSelectOption<undefined>>(v => ({
                                value: v,
                                label: props.lang[v],
                                className: v,
                                disabled: v.toLowerCase() === "unprocessed"
                            }))}
                        />
                        <GestTextArea fullw name="odi" label="ODI" defaultValue={checkNullValues(props.intervention.odi)} />
                    </fieldset>

                    {props.config.session?.userData?.custom_data &&
                        <fieldset className="section" id="customData">
                            <legend><Settings /> {props.lang?.custom_data}</legend>

                            {Object
                                .keys(JSON.parse(props.config.session.userData.custom_data))
                                .map(k => <GestTextArea key={`${customDataPrefix}${k}`} fullw name={`${customDataPrefix}${k}`} label={props.lang?.[k] ?? k} defaultValue={checkNullValues(props.intervention.custom_data ? JSON.parse(props.intervention.custom_data)[k] : "")} />)}
                        </fieldset>
                    }
                </>}

            <GestSaveButton alignment='right' submit label={props.lang.save} loading={interventionEditLoading} />
        </form>;
};
export default React.memo(InterventionEditForm);
